import Mat4 from 'gl-mat4';
import Vec3 from 'gl-vec3';
import Vec2 from 'gl-vec2';

import { lerp, lerpAngle, lerpVec3 } from './lerp';
import makePlayerSvg from './player-svg';
import makeGroundedState from './player-grounded';
import makeJumpingState from './player-jumping';
import makeSkidJumpingState from './player-skidjumping';

const makePlayer = (getFloorIntersection, getWallIntersection) => {
  const [svg, svgData] = makePlayerSvg();
  
  const modes = {
    'grounded': makeGroundedState(getFloorIntersection, getWallIntersection),
    'jumping': makeJumpingState(getFloorIntersection, getWallIntersection),
    'skidjumping': makeSkidJumpingState(getFloorIntersection, getWallIntersection),
  };

  const initialState = Object.values(modes).reduce((accum, { initialState }) => ({ ...accum, ...initialState }), {
    mode: 'grounded',
    position: Vec3.create(),
    velocity: Vec3.create(),
    facing: 0,
    jumped: false,
    shadowNormal: Vec3.fromValues(0, 1, 0),
    shadowDistance: 0,
    walk: 0,
    walkScale: 0,
  });
  
  const update = (state, dt, input) => {
    const lastMode = state.mode;
    state = modes[state.mode].update(state, dt, input);
    
    // reset the previous mode when entering a new one
    if (state.mode !== lastMode) {
      Object.assign(state, modes[lastMode].initialState);
    }
    
    return state;
  };
  
  const interpolate = (state, nextState, factor) => {
    const { base, leftLeg, rightLeg, shadow, setShadowOpacity } = svgData;
    
    const position = Vec3.lerp([], state.position, nextState.position, factor);
    const facing = lerpAngle(state.facing, nextState.facing, factor);
    
    const shadowNormal = Vec3.lerp([], state.shadowNormal, nextState.shadowNormal, factor);
    const shadowDistance = lerp(state.shadowDistance, nextState.shadowDistance, factor);
    const shadowIntensity = 1/((shadowDistance+5)/5);
    
    Mat4.fromRotation(base, facing, [0, 1, 0]);
    Vec3.transformMat4(shadowNormal, shadowNormal, Mat4.invert([], base));
    Mat4.multiply(base, Mat4.fromTranslation([], position), base);
    
    const shadowScale = Math.pow(shadowIntensity, 2/3);
    Mat4.lookAt(shadow, [0, 0, 0], [0, -shadowNormal[2], shadowNormal[1]], shadowNormal);
    Mat4.scale(shadow, shadow, [shadowScale, shadowScale, shadowScale]);
    Mat4.multiply(shadow, Mat4.fromTranslation([], [0, -shadowDistance, 0]), shadow);
    setShadowOpacity(0.3 * shadowIntensity);
    
    Mat4.fromTranslation(leftLeg, [0, 1, 0]);
    Mat4.fromTranslation(rightLeg, [0, 1, 0]);
    const angle = Math.sin(lerpAngle(state.walk, nextState.walk, factor)) * lerp(state.walkScale, nextState.walkScale, factor);
    Mat4.rotateX(leftLeg, leftLeg, -angle);
    Mat4.rotateX(rightLeg, rightLeg, angle);
    Mat4.scale(leftLeg, leftLeg, [-1, 1, 1]);
  };
  
  return [svg, update, initialState, interpolate];
};

export default makePlayer;